import cn from 'classnames'
import {useEffect, useRef, useState} from 'react'

import {BTN_PRIMARY} from '../constants'
import {useQuestionTimer} from '../hooks'
import type {Question, QuestionAnswerTiming} from '../types'
import {ProgressBar} from './ProgressBar'
import {TimerBadge} from './TimerBadge'

const INVOICE_FIELDS = ['invoice-supplier-name', 'invoice-address', 'invoice-vat-id', 'invoice-iban'] as const

export const InvoiceMasterData = ({
  question,
  questionNumber,
  totalQuestions,
  onAnswerLocked,
  onAnswer,
}: {
  question: Question
  questionNumber: number
  totalQuestions: number
  onAnswerLocked: (answer: string, score: number, correct: boolean, timing: QuestionAnswerTiming) => void
  onAnswer: (answer: string, score: number, correct: boolean, timing: QuestionAnswerTiming) => void
}) => {
  const data = question.invoiceMasterData!
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
  const [answered, setAnswered] = useState(false)
  const [earnedScore, setEarnedScore] = useState(0)
  const [isCorrect, setIsCorrect] = useState(false)
  const [answerTiming, setAnswerTiming] = useState<QuestionAnswerTiming | null>(null)
  const startedAtRef = useRef(Date.now())

  const getAnswerTiming = (timedOut: boolean, remainingSeconds: number): QuestionAnswerTiming => ({
    elapsedMs: Date.now() - startedAtRef.current,
    remainingSeconds,
    timedOut,
  })

  const handleFieldClick = (fieldId: string) => {
    if (answered) {
      return
    }

    const correct = fieldId === data.correctFieldId
    const score = correct ? question.points : 0
    const timing = getAnswerTiming(false, remaining)

    setSelectedFieldId(fieldId)
    setAnswered(true)
    setIsCorrect(correct)
    setEarnedScore(score)
    setAnswerTiming(timing)
    onAnswerLocked(fieldId, score, correct, timing)
  }

  const handleTimerExpire = () => {
    if (answered) {
      return
    }

    setAnswered(true)
    setEarnedScore(0)
    setIsCorrect(false)

    const timing = getAnswerTiming(true, 0)
    setAnswerTiming(timing)
    onAnswerLocked('', 0, false, timing)
  }

  const {remaining, total} = useQuestionTimer(question.timerSeconds, answered, handleTimerExpire)

  const [entered, setEntered] = useState(false)
  const [exiting, setExiting] = useState(false)

  // Trigger enter animation shortly after mount
  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 50)
    return () => clearTimeout(t)
  }, [])

  const handleNext = () => {
    const timing = answerTiming ?? getAnswerTiming(false, remaining)

    if (questionNumber >= totalQuestions) {
      setExiting(true)
      setTimeout(() => onAnswer(selectedFieldId ?? '', earnedScore, isCorrect, timing), 500)
    } else {
      onAnswer(selectedFieldId ?? '', earnedScore, isCorrect, timing)
    }
  }

  const buttonLabel = questionNumber < totalQuestions ? 'Next question' : 'See results'
  const isRevealed = answered
  const correctFieldId = data.correctFieldId

  const fieldClasses = (fieldId: string) => {
    const isCorrectField = isRevealed && fieldId === correctFieldId

    return cn('group/field relative cursor-pointer px-4 py-3 transition-colors', {
      'hover:bg-moss-grey-500/10': !answered,
      'outline outline-1 outline-green-neon bg-green-neon/10': isCorrectField && isCorrect,
      'outline outline-1 outline-ui-red-450 bg-ui-red-450/10': isCorrectField && !isCorrect,
      'opacity-40': isRevealed && fieldId !== correctFieldId,
    })
  }

  const invoiceFieldValues: Record<string, {label: string; value: string}> = {
    'invoice-supplier-name': {label: 'Supplier', value: data.invoice.supplierName},
    'invoice-address': {label: 'Address', value: data.invoice.address},
    'invoice-vat-id': {label: 'VAT ID', value: data.invoice.vatId},
    'invoice-iban': {label: 'Bank account', value: data.invoice.iban},
  }

  return (
    <div className="flex min-h-screen flex-col px-6 py-8 md:mx-auto md:w-full md:max-w-3xl">
      <div className="mb-6">
        <ProgressBar current={questionNumber} total={totalQuestions} />
      </div>

      <div
        className="min-h-0 transition-opacity duration-500 ease-out md:h-[520px]"
        style={{opacity: !entered || exiting ? 0 : 1}}
      >
        <div className="mb-5">
          <div className="mb-1 flex items-center gap-2.5">
            <span className="text-12 font-medium uppercase tracking-[.08em] text-moss-grey-400">Level 2</span>
            <span className="flex items-center gap-1.5 text-10 text-grey-300">
              <span className="size-1 shrink-0 rounded-full bg-grey-300" />
              {question.points} points for the right answer
            </span>
          </div>
          <h2 className="text-18 font-medium leading-tight text-moss-white md:text-22">{question.question}</h2>
          <p className="mt-1 text-13 text-moss-grey-300">
            One field on the invoice doesn&apos;t match what&apos;s stored as supplier data in your system. Select the
            correct one.
          </p>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="flex-1 overflow-hidden rounded-xl border border-[#8e8b8b] bg-[#171717]">
            <div className="flex items-center justify-between bg-[#3d3c3c] px-4 py-5">
              <span className="text-12 font-semibold uppercase tracking-[.08em] text-grey-300">Invoice</span>
              <span className="text-12 font-semibold uppercase tracking-[.08em] text-grey-300">
                {data.invoice.invoiceNumber}
              </span>
            </div>

            <div className="px-4 py-3">
              <div className="flex items-baseline justify-between">
                <span className="text-12 text-moss-white">{data.invoice.date}</span>
                <span className="text-16 text-moss-white">{data.invoice.amount}</span>
              </div>
              <p className="mt-1 text-12 text-grey-300">{data.invoice.description}</p>
            </div>
            <div className="mx-4 border-t border-[#4a4a4a]" />

            <div>
              {INVOICE_FIELDS.map((fieldId, i) => {
                const field = invoiceFieldValues[fieldId]
                const isThisCorrect = isRevealed && fieldId === correctFieldId

                return (
                  <div key={fieldId}>
                    {i > 0 && <div className="mx-4 border-t border-[#4a4a4a]" />}
                    <div onClick={() => handleFieldClick(fieldId)} className={fieldClasses(fieldId)}>
                      {!answered && (
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded bg-[#3d3c3c] px-2 py-0.5 text-10 text-grey-300 opacity-0 transition-opacity group-hover/field:opacity-100">
                          Select
                        </span>
                      )}
                      <span className="text-12 font-semibold text-grey-200">{field.label}</span>
                      <p
                        className={cn('text-12', {
                          'text-grey-450': !isThisCorrect,
                          'text-green-neon font-semibold': isThisCorrect && isCorrect,
                          'text-ui-red-450 font-semibold': isThisCorrect && !isCorrect,
                        })}
                      >
                        {field.value}
                      </p>
                      {isThisCorrect && isCorrect && (
                        <span className="mt-1 block text-right text-14 font-semibold text-green-neon">
                          +{earnedScore} PTS
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="border-t border-dashed border-[#4a4a4a] md:self-stretch md:border-l md:border-t-0" />

          <div className="flex-1">
            {answered ? (
              <div className="animate-[fadeIn_0.5s_ease-out] rounded-lg border border-grey-900 p-5">
                <div className="mb-3 flex items-center gap-2">
                  <div className="size-1.5 rounded-full bg-green-neon" />
                  <span className="text-12 font-semibold uppercase tracking-[.1em] text-green-neon">
                    Finance Insight
                  </span>
                </div>
                <p className="text-[15px] leading-relaxed text-moss-grey-300">{question.mossInsight.body}</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-[#3d3c3c] bg-[#171717]">
                <div className="flex items-center gap-2 bg-[#3d3c3c] px-4 py-5">
                  <div className="size-2 rounded-full bg-green-neon/50" />
                  <span className="text-12 font-semibold uppercase tracking-[.08em] text-grey-300">
                    Supplier Master Data
                  </span>
                  <span className="ml-auto inline-flex rounded-full bg-[#4a4a4a] px-2 py-0.5 text-10 font-medium uppercase text-grey-450">
                    Verified
                  </span>
                </div>

                <div className="divide-y divide-[#4a4a4a] px-4">
                  <div className="py-3">
                    <span className="text-12 font-semibold text-grey-200">Supplier</span>
                    <p className="text-12 text-grey-450">{data.masterData.supplierName}</p>
                  </div>
                  <div className="py-3">
                    <span className="text-12 font-semibold text-grey-200">Address</span>
                    <p className="text-12 text-grey-450">{data.masterData.address}</p>
                  </div>
                  <div className="py-3">
                    <span className="text-12 font-semibold text-grey-200">VAT ID</span>
                    <p className="text-12 text-grey-450">{data.masterData.vatId}</p>
                  </div>
                  <div className="py-3">
                    <span className="text-12 font-semibold text-grey-200">Bank account</span>
                    <p className="text-12 text-grey-450">{data.masterData.iban}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="flex items-center justify-between pt-4 transition-opacity duration-500 ease-out"
        style={{opacity: !entered || exiting ? 0 : 1}}
      >
        <TimerBadge remaining={remaining} total={total} />
        <button onClick={handleNext} disabled={!answered} className={BTN_PRIMARY}>
          {buttonLabel}
        </button>
      </div>
    </div>
  )
}
